// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuestionnaireResponse } from '@intakeform/fhir';
import { apiFetch } from './client.js';

export type AutofillStatus =
  'pending_admin_review' | 'pending_doctor_approval' | 'approved' | 'rejected' | 'published';

export interface AutofillJobSummary {
  id: string;
  organizationId: string;
  questionnaireId: string;
  patientRef: string;
  emrAdapterId: string;
  status: AutofillStatus;
  draftQr: QuestionnaireResponse;
  emrContextSnapshot: { patient: EmrPatientSummary };
  prefillFindings: {
    severity: 'error' | 'warning' | 'info';
    code: string;
    message: string;
    linkId?: string;
  }[];
  rejectionReason: string | null;
  createdAt: string;
  adminReviewedAt: string | null;
  doctorApprovedAt: string | null;
  publishedAt: string | null;
}

export interface EmrPatientSummary {
  id: string;
  displayName: string;
  birthDate?: string;
  gender?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export const autofillListKey = (status?: AutofillStatus) =>
  ['autofill', 'list', status ?? 'all'] as const;
export const autofillJobKey = (id: string) => ['autofill', 'job', id] as const;
export const emrPatientsKey = (q?: string, mrn?: string) =>
  ['emr', 'patients', q ?? '', mrn ?? ''] as const;

/**
 * Debounce window for patient search. Each distinct `q` is its own query key,
 * so without this every keystroke fired a request that travels
 * browser -> api -> Astrum over the internet; an 8-character surname cost up
 * to 8 round trips of which only the last mattered
 * (intakeform-preview-3nx).
 */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Patient lookup for the picker (intakeform-preview-200.7.7).
 *
 * Two searches, not one box with clever parsing. `q` is a guess the adapter is
 * free to interpret — name, email, identifier — because a clinician reads the
 * results. `mrn` asserts one exact record, and the API matches it exactly or
 * returns nothing rather than widening to a name.
 *
 * An MRN takes precedence when both are present, mirroring the server, so the
 * screen can never show results from a different kind of search than the one
 * the user believes they ran.
 */
export function useEmrPatients(q: string, mrn = '') {
  const [debounced, setDebounced] = useState(q);
  const [debouncedMrn, setDebouncedMrn] = useState(mrn);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(q);
      setDebouncedMrn(mrn);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
    };
  }, [q, mrn]);

  return useQuery<EmrPatientSummary[]>({
    queryKey: emrPatientsKey(debounced, debouncedMrn),
    queryFn: async () => {
      const exact = debouncedMrn.trim();
      const url =
        exact !== ''
          ? `/api/emr/patients?mrn=${encodeURIComponent(exact)}`
          : debounced.trim() === ''
            ? '/api/emr/patients'
            : `/api/emr/patients?q=${encodeURIComponent(debounced)}`;
      const res = await apiFetch<{ items: EmrPatientSummary[] }>(url);
      return res.items;
    },
    // A patient picker does not need second-by-second freshness, and
    // backspacing through a query should reuse what we already fetched
    // rather than re-hitting Astrum.
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAutofillJobs(status?: AutofillStatus) {
  return useQuery<AutofillJobSummary[]>({
    queryKey: autofillListKey(status),
    queryFn: async () => {
      const url = status === undefined ? '/api/autofill' : `/api/autofill?status=${status}`;
      const res = await apiFetch<{ items: AutofillJobSummary[] }>(url);
      return res.items;
    },
  });
}

/**
 * The identity link behind a prefill (intakeform-preview-200.6): which EMR
 * record this patient is tied to, and whether a human confirmed it.
 */
export interface EmrLink {
  id: string;
  patientId: string;
  emrAdapterId: string;
  externalPatientId: string;
  matchMethod: string;
  confirmedAt: string | null;
}

interface AutofillJobResponse {
  job: AutofillJobSummary;
  emrLink: EmrLink | null;
}

/**
 * One query, two views. Both hooks below share `autofillJobKey`, so React
 * Query dedupes them into a single request — the detail route can ask for the
 * job and the link independently without a second round trip, and callers that
 * only want the job (the print route) stay untouched.
 */
function autofillJobQuery(id: string | undefined) {
  return {
    queryKey: id !== undefined ? autofillJobKey(id) : (['autofill', 'job', 'noop'] as const),
    enabled: id !== undefined,
    queryFn: async (): Promise<AutofillJobResponse> =>
      apiFetch<AutofillJobResponse>(`/api/autofill/${String(id)}`),
  };
}

export function useAutofillJob(id: string | undefined) {
  return useQuery({
    ...autofillJobQuery(id),
    select: (res: AutofillJobResponse) => res.job,
  });
}

export function useAutofillJobEmrLink(id: string | undefined) {
  return useQuery({
    ...autofillJobQuery(id),
    select: (res: AutofillJobResponse) => res.emrLink,
  });
}

/**
 * Break a patient's EMR correlation. The only destructive verb on links:
 * they are created implicitly when a clinician picks a record, so the thing
 * a human actually needs is a way to undo a wrong one.
 */
export function useUnlinkPatientEmr(jobId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<EmrLink, Error, string>({
    mutationFn: async (linkId) => {
      const res = await apiFetch<{ link: EmrLink }>(`/api/patient-links/${linkId}`, {
        method: 'DELETE',
        body: {},
      });
      return res.link;
    },
    onSuccess: async () => {
      if (jobId !== undefined) {
        await qc.invalidateQueries({ queryKey: autofillJobKey(jobId) });
      }
    },
  });
}

export function useCreateAutofillJob() {
  const qc = useQueryClient();
  return useMutation<AutofillJobSummary, Error, { questionnaireId: string; patientId: string }>({
    mutationFn: async (input) => {
      const res = await apiFetch<{ job: AutofillJobSummary }>('/api/autofill', {
        method: 'POST',
        body: input,
      });
      return res.job;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['autofill', 'list'] });
    },
  });
}

export interface PatchAutofillInput {
  id: string;
  draftQr?: QuestionnaireResponse;
  transition?: 'admin_approve' | 'doctor_approve' | 'reject';
  reason?: string;
}

export function usePatchAutofillJob() {
  const qc = useQueryClient();
  return useMutation<AutofillJobSummary, Error, PatchAutofillInput>({
    mutationFn: async ({ id, ...body }) => {
      const res = await apiFetch<{ job: AutofillJobSummary }>(`/api/autofill/${id}`, {
        method: 'PATCH',
        body,
      });
      return res.job;
    },
    onSuccess: (job) => {
      qc.setQueryData(autofillJobKey(job.id), job);
      void qc.invalidateQueries({ queryKey: ['autofill', 'list'] });
    },
  });
}
